<template>
  <div class="global-chat-wrapper">
    <!-- Chat Button -->
    <div
      class="chat-widget-button closechat_btn"
      style="text-align: center"
      @click="toggleChat"
    >
      <i class="fa fa-robot" /><span>Ask to AI</span>
    </div>

    <!-- Chat Window -->
    <div
      v-show="isChatVisible"
      class="chat-widget_wrap"
      style="display: block"
    >
      <div class="chatbot-container">
        <!-- AI Header & New Conversation Button -->
        <div class="chat-header d-flex justify-content-between align-items-center">
          <strong><i class="fa fa-robot me-2" /> PetQueen AI</strong>
          <button
            class="btn-new-chat"
            title="Start New Conversation"
            @click="startNewConversation"
          >
            <i class="fa fa-plus" /> New
          </button>
        </div>

        <div
          ref="chatMessages"
          class="chat-messages"
        >
          <div
            v-for="(message, index) in messages"
            :key="index"
            :class="['message', message.sender === 'user' ? 'user-message' : 'bot-message']"
          >
            <p v-html="formatMessage(message.text)" />
          </div>
          <div
            v-if="isLoading"
            class="bot-message loading"
          >
            <span class="dot" />
            <span class="dot" />
            <span class="dot" />
          </div>
        </div>
        <div class="chat-input">
          <input
            v-model="userInput"
            type="text"
            placeholder="Ask about pet related..."
            :disabled="isLoading"
            @keyup.enter="sendMessage"
          >
          <button
            :disabled="isLoading || !userInput.trim()"
            @click="sendMessage"
          >
            <i class="fa fa-paper-plane" />
          </button>
        </div>
        <div class="disclaimer-text">
          AI suggestions are not medical advice. Please consult a vet for health concerns.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, nextTick, onUnmounted, watch } from 'vue';
  import { db, functions } from '@/firebase/index.js';
  import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
  import { getAuth } from 'firebase/auth';
  import { httpsCallable } from 'firebase/functions';

  // Reactive state
  const userInput = ref('');
  const messages = ref([]);
  const isLoading = ref(false);
  const chatMessages = ref(null);
  const isChatVisible = ref(false);

  const toggleChat = () => {
    isChatVisible.value = !isChatVisible.value;
    saveLocalSession();
  };

  watch(isChatVisible, (newVal) => {
    if (newVal) {
      nextTick(() => {
        scrollToBottom();
      });
    }
  }, { flush: 'post' });

  watch(messages, () => {
    if (isChatVisible.value) {
      scrollToBottom();
    }
  }, { deep: true, flush: 'post' });

  // Observer for scrolling
  let chatResizeObserver = null;

  // Session state
  let localSession = {
    docId: null,
    updatedAt: null
  };

  const SESSION_KEY = 'petqueen_ai_session';
  const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

  // Simple Markdown Parser for Links, Bold, and Line Breaks
  const formatMessage = (text) => {
    if (!text) return '';
    let html = text;
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="chat-link">$1</a>');
    html = html.replace(/\n/g, '<br/>');
    return html;
  };

  const loadLocalSession = () => {
    const saved = localStorage.getItem(SESSION_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const isExpired = (Date.now() - parsed.updatedAt) > TWENTY_FOUR_HOURS;
      if (!isExpired) {
        messages.value = parsed.messages || [];
        localSession.docId = parsed.docId || null;
        localSession.updatedAt = parsed.updatedAt;
        isChatVisible.value = parsed.isChatVisible || false;
        return true;
      } else {
        localStorage.removeItem(SESSION_KEY);
      }
    }
    
    // Default start
    messages.value = [
      { text: "Hello! I'm PetQueenAI, your pet care assistant. How can I help you today?", sender: 'bot' }
    ];
    return false;
  };

  const startNewConversation = () => {
    // Mesajları sıfırla ve yeni belge olması için docId'yi null yap
    messages.value = [
      { text: "Hello! I'm PetQueenAI, your pet care assistant. How can I help you today?", sender: 'bot' }
    ];
    localSession.docId = null;
    localSession.updatedAt = Date.now();
    saveLocalSession();
  };

  const saveLocalSession = () => {
    localStorage.setItem(SESSION_KEY, JSON.stringify({
      messages: messages.value,
      docId: localSession.docId,
      updatedAt: Date.now(),
      isChatVisible: isChatVisible.value
    }));
  };

  const logErrorToDB = async (errMessage, stack) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      await addDoc(collection(db, 'ai_errors'), {
        userId: user ? user.uid : 'anonymous',
        error_message: errMessage,
        stack_trace: stack,
        timestamp: serverTimestamp()
      });
    } catch (e) {
      console.error("Failed to log error to DB", e);
    }
  };

  const syncToFirestore = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return; // Only save to DB if logged in

    try {
      if (!localSession.docId) {
        // Create new document
        const firstUserMsg = messages.value.find(m => m.sender === 'user');
        let title = "New Conversation";
        if (firstUserMsg) {
          title = firstUserMsg.text.split(' ').slice(0, 4).join(' ') + '...';
        }

        const docRef = await addDoc(collection(db, 'ai_conversations'), {
          userId: user.uid,
          title: title,
          messages: messages.value,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        localSession.docId = docRef.id;
        saveLocalSession();
      } else {
        // Update existing document
        await updateDoc(doc(db, 'ai_conversations', localSession.docId), {
          messages: messages.value,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error("Error syncing to Firestore:", error);
    }
  };

  // Helper to get formatted history for the backend
  const getFormattedHistory = () => {
    return messages.value
      .filter(m => m.text !== "Hello! I'm PetQueenAI, your pet care assistant. How can I help you today?")
      .map(m => ({
        role: m.sender === 'user' ? 'user' : 'model',
        content: [{ text: m.text }]
      }));
  };

  // Listen for external requests to load a specific conversation
  const handleLoadChat = (event) => {
    const convo = event.detail; // Expecting full conversation object
    messages.value = convo.messages || [];
    localSession.docId = convo.id;
    localSession.updatedAt = Date.now();
    saveLocalSession();
    isChatVisible.value = true;
    setTimeout(scrollToBottom, 100);
  };

  onMounted(async () => {
    window.addEventListener('load-ai-chat', handleLoadChat);
    
    try {
      loadLocalSession();

      if (isChatVisible.value) {
        nextTick(() => {
          scrollToBottom();
        });
      }

      // Akıllı tarayıcı gözlemcisi (ResizeObserver):
      // Element gizli (display:none) durumdan görünür duruma geçtiğinde
      // veya içeriği değişip boyutu hesaplandığında otomatik tetiklenir.
      if (chatMessages.value && window.ResizeObserver) {
        chatResizeObserver = new ResizeObserver(() => {
          scrollToBottom();
        });
        chatResizeObserver.observe(chatMessages.value);
      }

      console.log('Gemini model initialized successfully');
    } catch (error) {
      console.error('Error initializing Gemini:', error);
      messages.value.push({ text: "I'm having trouble connecting. Please try again later.", sender: 'bot' });
    }
  });

  onUnmounted(() => {
    window.removeEventListener('load-ai-chat', handleLoadChat);
    if (chatResizeObserver) {
      chatResizeObserver.disconnect();
    }
  });

  // Function to send message to Genkit backend
  const sendMessage = async () => {
    const userMessage = userInput.value.trim();
    if (!userMessage || isLoading.value) return;

    messages.value.push({ text: userMessage, sender: 'user' });
    userInput.value = '';
    isLoading.value = true;
    saveLocalSession();
    await nextTick(); scrollToBottom();

    try {
      // Call Firebase Genkit Function
      const chatFlow = httpsCallable(functions, 'petQueenChatFlow');
      
      const history = getFormattedHistory();
      // Remove the last user message from history as it is being sent as prompt
      if (history.length > 0 && history[history.length - 1].role === 'user') {
        history.pop(); 
      }

      const result = await chatFlow({ 
        message: userMessage, 
        history: history 
      });

      const responseText = result.data.text;
      
      messages.value.push({ text: responseText, sender: 'bot' });

      saveLocalSession();
      syncToFirestore(); // Sync to DB in background

    } catch (error) {
      console.error('Error from AI backend:', error);
      logErrorToDB(error.message, error.stack);
      messages.value.push({
        text: "Sistemde bir sorun oluştu, şu an hizmet veremiyorum.",
        sender: 'bot',
      });
      saveLocalSession();
    } finally {
      isLoading.value = false;
      await nextTick(); scrollToBottom();
    }
  };

  const scrollToBottom = () => {
    if (chatMessages.value) {
      chatMessages.value.scrollTop = chatMessages.value.scrollHeight;
      const lastChild = chatMessages.value.lastElementChild;
      if (lastChild) {
        lastChild.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  };
</script>

<style scoped>
  .chat-widget_wrap {
    width: 330px !important; /* %10 daha geniş (300 -> 330) */
    height: 480px !important; /* %20 daha yüksek (400 -> 480) */
    right: 50px !important; /* Standart masaüstü boşluğu */
  }

  @media (max-width: 768px) {
    .chat-widget-button {
      right: 15px !important; /* Butonu sağdan 15px hizala */
      bottom: 75px !important; /* Alt menüye daha da yaklaştırdık (95px -> 75px) */
    }
    .chat-widget_wrap {
      right: 15px !important; /* Mobilde boşluğu azalt */
      width: calc(100vw - 30px) !important; /* Taşmayı önle */
      max-width: 330px !important; 
      bottom: 120px !important; /* Butonun (75px) hemen üzerinde (40px buton + 5px boşluk) başlasın */
    }
  }

  .chatbot-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    background: white;
  }

  .chat-header {
    background-color: #7f74c6;
    color: white;
    padding: 12px 15px;
    font-size: 15px;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    z-index: 2;
  }

  .btn-new-chat {
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
    border: none;
    border-radius: 20px;
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn-new-chat:hover {
    background-color: rgba(255, 255, 255, 0.35);
  }

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .message {
    max-width: 80%;
    padding: 12px 15px;
    border-radius: 18px;
    word-wrap: break-word;
  }

  .user-message {
    align-self: flex-end;
    background-color: #7f74c6;
    color: #f5f5f5;
    border-radius: 18px 18px 5px 18px;
  }

  .user-message p {
    color: #f5f5f5 !important;
    margin: 0;
  }

  .bot-message {
    align-self: flex-start;
    background-color: #f8bbd0;
    color: #111;
    border-radius: 18px 18px 18px 5px;
  }

  .bot-message p {
    color: #111 !important;
    margin: 0;
    line-height: 1.5;
  }

  /* Chat link styles applied to v-html parsed links */
  :deep(.chat-link) {
    color: #0056b3;
    text-decoration: underline;
    font-weight: bold;
  }
  :deep(.chat-link:hover) {
    color: #003d82;
  }

  .chat-input {
    display: flex;
    padding: 15px;
    border-top: 1px solid #e9ecef;
    background: white;
  }

  .chat-input input {
    flex: 1;
    padding: 12px 15px;
    border: 1px solid #ced4da;
    border-radius: 25px;
    outline: none;
    margin-right: 10px;
  }

  .chat-input button {
    background: #7f74c6;
    color: white;
    border: none;
    border-radius: 50%;
    width: 45px;
    height: 45px;
    cursor: pointer;
    transition: background 0.3s;
  }

  .chat-input button:hover {
    background: #6a60b8;
  }

  .chat-input button:disabled {
    background-color: #ddd;
    cursor: not-allowed;
  }

  .disclaimer-text {
    font-size: 0.75rem;
    color: #888;
    text-align: center;
    padding: 0 15px 10px;
    margin-top: -5px;
  }

  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 60px;
    min-height: 40px;
  }

  .dot {
    width: 8px;
    height: 8px;
    background: #555;
    border-radius: 50%;
    margin: 0 3px;
    animation: dot-pulse 1.5s infinite ease-in-out;
  }

  .dot:nth-child(2) {
    animation-delay: 0.2s;
  }

  .dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes dot-pulse {
    0%,
    100% {
      transform: scale(0.8);
      opacity: 0.6;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
  }
</style>
